import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  CheckCircle,
  Mail,
  RefreshCw,
  Shield,
  Clock,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Password Reset Success Page - World-Class UI Implementation
 *
 * Features:
 * - 2-column layout (message + visual content)
 * - SALONHUB logo integration
 * - shadcn/ui Design System
 * - Responsive design (mobile: 1-column, desktop: 2-column)
 * - WCAG 2.1 AA compliant
 * - Smooth micro-interactions
 * - Email resend functionality
 *
 * @returns Password reset success page component
 */
const PasswordResetSuccess: React.FC = () => {
  usePageTitle('パスワードリセット完了');
  const navigate = useNavigate();
  const location = useLocation();
  const [resending, setResending] = useState(false);

  const email = location.state?.email || '';

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleResendEmail = async () => {
    setResending(true);

    try {
      // TODO: 実際の再送信APIと統合
      // const response = await authService.resetPassword({ email });

      // モック実装
      setTimeout(() => {
        setResending(false);
        // 成功のフィードバック（トースト通知等）
      }, 1500);
    } catch (err) {
      setResending(false);
      console.error('再送信エラー:', err);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      {/* LEFT COLUMN: Success Message */}
      <div className="flex flex-col justify-center p-4 md:p-8 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="max-w-[480px] mx-auto w-full">
            {/* SALONHUB Logo */}
            <img
              src="/assets/logos/salon-hub-logo.png"
              alt="SALON HUB"
              className="w-full max-w-[175px] h-auto mb-6 transition-transform duration-300 cursor-pointer hover:scale-105"
              onClick={handleBackToLogin}
            />

            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring", stiffness: 200 }}
            >
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-primary" />
                </div>
              </div>
            </motion.div>

            {/* Title */}
            <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 mb-2 leading-tight text-center">
              メールを送信しました
            </h1>

            <p className="text-base text-gray-600 leading-relaxed mb-6 text-center">
              パスワードリセット用のリンクを以下のメールアドレスにお送りしました
            </p>

            {/* Email Display */}
            <div className="flex items-center justify-center gap-2 p-3 mb-6 bg-primary/5 rounded-lg border border-primary/20">
              <Mail className="text-primary w-6 h-6" />
              <span className="text-primary font-semibold text-lg break-all">
                {email || 'メールアドレスが取得できませんでした'}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <Button
                className="w-full min-h-[56px] py-2.5"
                onClick={handleBackToLogin}
              >
                ログインページに戻る
              </Button>

              <Button
                className="w-full min-h-[56px] py-2.5"
                variant="outline"
                onClick={handleResendEmail}
                disabled={resending || !email}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {resending ? 'メール再送信中...' : 'メールを再送信'}
              </Button>
            </div>

            {/* Help Text */}
            <p className="text-sm text-gray-500 leading-relaxed text-center mt-4">
              メールが届かない場合は、迷惑メールフォルダもご確認ください
            </p>
          </div>
        </motion.div>
      </div>

      {/* RIGHT COLUMN: Visual Content */}
      <div className="hidden md:flex flex-col justify-center items-center p-8 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white relative overflow-hidden">
        <div className="absolute top-[-50%] right-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_70%)] animate-[pulse_8s_ease-in-out_infinite]" />

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="z-10 max-w-[500px] w-full"
        >
          {/* Main Message */}
          <h2 className="scroll-m-20 text-2xl md:text-3xl font-bold tracking-tight mb-8 text-center leading-[1.3] [text-shadow:0_2px_4px_rgba(0,0,0,0.1)]">
            次のステップ
          </h2>

          {/* Next Steps */}
          <div className="mt-6">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="mb-5 flex items-start gap-3">
                <div className="min-w-[48px] min-h-[48px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-[10px]">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h6 className="font-semibold text-lg mb-1">
                    1. メールを確認
                  </h6>
                  <p className="text-sm opacity-90 leading-relaxed">
                    送信されたメールを開いてください
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <div className="mb-5 flex items-start gap-3">
                <div className="min-w-[48px] min-h-[48px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-[10px]">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <div>
                  <h6 className="font-semibold text-lg mb-1">
                    2. リンクをクリック
                  </h6>
                  <p className="text-sm opacity-90 leading-relaxed">
                    メール内のリセット用リンクをクリックしてください
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-[48px] min-h-[48px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-[10px]">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <div>
                  <h6 className="font-semibold text-lg mb-1">
                    3. 新しいパスワードを設定
                  </h6>
                  <p className="text-sm opacity-90 leading-relaxed">
                    安全な新しいパスワードを作成してください
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Security Notes */}
          <div className="mt-8 p-3 bg-white/10 rounded-lg backdrop-blur-[10px]">
            <div className="text-sm opacity-95 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>リンクの有効期限は24時間です</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span>セキュリティ上の理由により、リンクは1回のみ使用可能です</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PasswordResetSuccess;
